import { CustomMouseEvent } from '../controls';
import { GeocentricCoordinate, sphereRaycast } from '../projections';
import { setPointScale } from '../utilities';
import {
  MeasurementArea,
  MeasurementVolume,
  MeasurementController,
  MeasureType
} from '.';
import {
  Mesh,
  Scene,
  SphereGeometry,
  MeshBasicMaterial,
  LineBasicMaterial,
  Line,
  BufferGeometry
} from 'three';
import { setViewerTooltip } from '../../viewer';
import { RayCaster } from '../ray-caster';

export class MeasureEndpoint extends Mesh {
  public measurementVertex;
  public measurementID;

  constructor(geometry, material, index, id) {
    super(geometry, material);
    this.measurementVertex = index;
    this.measurementID = id;
  }
}

export class TemporaryEndpoint extends Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }
}

export class MeasurementSceneView {
  private controller: MeasurementController;
  private scene: Scene;

  private sphereBuffer = 1.2;
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);
  private sphereMaterialStandard = new MeshBasicMaterial({color: 0xffffff});
  private sphereMaterialHover = new MeshBasicMaterial({color: 0xffcc33});
  private lineMaterialStandard = new LineBasicMaterial({color: 0x008B8B});
  private lineMaterialHover = new LineBasicMaterial({color: 0xffcc33});

  private measurements = [];
  private temporaryEndpoints = [];
  private temporaryLines = [];
  public hoveredPoint = null;
  public dragObject: MeasureEndpoint = null;
  private dragValid = false;

  private dragTooltip = [
    "Click and hold to move vertex"
  ];

  private restrictedTooltip = [
    "Unable to move this measurement type"
  ];

  constructor(controller, scene) {
    this.controller = controller;
    this.scene = scene;
  }

  get viewer() {
    return this.controller.viewer;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (!this.controller.enabled) {
      return false;
    }

    if (!event.mouseMoved && !event.mouseDown) {
      this.checkEndpointSelected(event);
    }

    if (this.controller.measuring) {
      let coordinate = this.controller.getCoordinate(event);
      this.addActiveEndpoint(coordinate);
    }

    if (this.dragObject) {
      this.setPointGrabTooltip();

      if (event.mouseDown) {
        if (event.isLeftClick) {
          let coordinate = this.controller.getCoordinate(event);
          this.updateMeasureEndpoint(coordinate);
          return true;
        }
      }
    } else {
      this.controller.updateMeasureText();
    }

    return false;
  }

  updateMeasureEndpoint(coordinate) {
    if (!this.dragObject) {
      return;
    }

    if (!coordinate) {
      return;
    }

    let id = this.dragObject.measurementID;
    let vertexIndex = this.dragObject.measurementVertex;
    let measurement = this.controller.measurementGroup.measurements.find(
      measurement => measurement.id === id
    );

    if (!measurement) {
      return;
    }

    if (measurement instanceof MeasurementVolume) {
      measurement.stopVolumeCalculation();
    }

    this.dragValid = true;

    // Update our measurement vertex value
    let vertex = new GeocentricCoordinate().fromScene(coordinate);
    measurement.vertices[vertexIndex] = vertex;

    // Remove + redraw the line
    this.removeByID(id);
    this.draw(measurement);
  }

  measureGrabFinished() {
    if ((!this.dragObject) || (!this.dragValid)) {
      return;
    }

    this.controller.refreshMeasurementList();
  }

  checkEndpointSelected(event) {
    let controller = this.controller;
    if (!controller.editable) {
      return;
    }

    if (controller.measurementBuilder.activeMeasurement) {
      return;
    }

    let targets = this.scene.children
      .filter(x => x instanceof MeasureEndpoint);

    let raycaster = new RayCaster(this.viewer, event);
    let objects = raycaster.intersectObjects(targets, {
      minDistance: 0.10,
      sortByRay: true
    });

    let draggable = objects.length > 0;
    let dragObject = draggable ? objects[0].object : null;
    this.setDragObject(dragObject);
  }

  setDragObject(dragObject) {
    // Image-only measurements can't be dragged
    if (this.controller.isImageOnly) {
      dragObject = null;
    }

    this.dragObject = dragObject;

    let draggable = dragObject ? true : false;
    let controls = this.controller.controls;
    controls.setMovementState(!draggable);

    if (dragObject) {
      this.setPointGrabTooltip();
    }
  }

  setPointGrabTooltip() {
    setViewerTooltip(this.dragTooltip);
  }

  setRestrictedPointTooltip() {
    setViewerTooltip(this.restrictedTooltip);
  }

  setTooltipText(measurement: MeasureType, morepoints = false) {
    let text = [] as string[];

    if (morepoints) {
      let endTooltipCount = measurement.minVertexRequired - 1;
      endTooltipCount = Math.max(2, endTooltipCount);

      if (measurement.vertices.length < endTooltipCount) {
        text = this.controller.isImageOnly
          ? measurement.moreTooltipMulti
          : measurement.moreTooltip;
      } else {
        text = this.controller.isImageOnly
          ? measurement.endTooltipMulti
          : measurement.endTooltip;
      }
    } else {
      text = this.controller.isImageOnly
        ? measurement.startTooltipMulti
        : measurement.startTooltip;
    }

    setViewerTooltip(text);
  }

  setPointScale(mesh) {
    setPointScale(this.viewer, mesh, this.sphereBuffer);
  }

  addTemporaryPoint(coordinate) {
    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.temporaryEndpoints.push(sphere);
  }

  addActiveEndpoint(coordinate) {
    if (coordinate === null) {
      return;
    }

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.hoveredPoint = sphere;

    // Remove all previous lines
    for (let temporaryLine of this.temporaryLines) {
      this.scene.remove(temporaryLine);
    }
    this.temporaryLines = [];

    // Re-draw the entire temporary line
    let points = [...this.temporaryEndpoints, this.hoveredPoint];
    for (var i=1;i<points.length;i++) {
      let line = this.createLine(points[i].position, points[i-1].position);
      this.scene.add(line);
      this.temporaryLines.push(line);
    }
  }

  createEndpoint(coordinates, index, id) {
    let sphere = new MeasureEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard, index, id);

    sphere.position.copy(coordinates);
    this.setPointScale(sphere);

    sphere.geometry.computeBoundingSphere();
    sphere.geometry.boundingSphere.radius *= this.sphereBuffer;
    sphere.raycast = sphereRaycast;

    return sphere;
  }

  createTempEndpoint(coordinate) {
    let sphere = new TemporaryEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard);

    sphere.position.copy(coordinate);
    this.setPointScale(sphere);

    return sphere;
  }

  createLine(start, end) {
    const vertices = [start.clone(), end.clone()];
    const geometry = new BufferGeometry().setFromPoints(vertices);
    return new Line(geometry, this.lineMaterialStandard);
  }

  updateMaterial(id, hover) {
    let material;

    let measurement = this.measurements.find(measurement => measurement.id === id);
    if (measurement) {
      for (let object of measurement.lines) {
        material = hover ? this.lineMaterialHover : this.lineMaterialStandard;
        object.material = material;
        object.material.needsUpdate = true;
      }
      for (let object of measurement.points) {
        material = hover ? this.sphereMaterialHover : this.sphereMaterialStandard;
        object.material = material;
        object.material.needsUpdate = true;
      }
    }
  }

  draw(measurement) {
    let lines = [];
    let points = [];
    let point;
    let line;
    let vertices = measurement.vertices;

    for (var i = 0; i < vertices.length; i++) {
      let vertexA = measurement.vertices[i].toScene();
      point = this.createEndpoint(vertexA, i, measurement.id);
      this.scene.add(point);
      points.push(point);

      if (i >= 1) {
        let vertexB = measurement.vertices[i - 1].toScene();
        line = this.createLine(vertexA, vertexB);
        this.scene.add(line);
        lines.push(line);
      }
    }

    // Extra steps for area (close polygon + calculate surface)
    if (measurement instanceof MeasurementArea) {
      let vertexA = measurement.vertices[vertices.length - 1].toScene();
      let vertexB = measurement.vertices[0].toScene();
      line = this.createLine(vertexA, vertexB);
      this.scene.add(line);
      lines.push(line);

      measurement.generateSurface();
    }

    this.measurements.push({
      id: measurement.id,
      lines: lines,
      points: points
    });
  }

  removeTemporaryObjects() {
    for (let temporaryEndpoint of this.temporaryEndpoints) {
      this.scene.remove(temporaryEndpoint);
    }

    for (let temporaryLine of this.temporaryLines) {
      this.scene.remove(temporaryLine);
    }

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    this.temporaryEndpoints = [];
    this.temporaryLines = [];
    this.hoveredPoint = null;

  }

  removeByID(id) {
    let measurement = this.measurements.find(measurement => measurement.id === id);
    if (measurement) {
      for (let object of measurement.lines) {
        this.scene.remove(object);
      }
      for (let object of measurement.points) {
        this.scene.remove(object);
      }
      this.measurements = this.measurements.filter(measurement => measurement.id !== id);
    }

    let measurementGroup = this.controller.measurementGroup;
    let measurementObject = measurementGroup.measurements.find(
      measurement => measurement.id === id
    );

    measurementObject.clear();
  }

  removeAll() {
    for (let measurement of this.measurements) {
      for (let object of measurement.lines) {
        this.scene.remove(object);
      }
      for (let object of measurement.points) {
        this.scene.remove(object);
      }
    }
    this.measurements = [];
  }

  update(changeDetector) {
    if (changeDetector === null) {
      return;
    }

    const update = changeDetector.changed;
    if (!update) {
      return;
    }

    this.temporaryEndpoints.forEach(mesh => {
      this.setPointScale(mesh);
    });

    this.measurements.forEach(measurement => {
      measurement.points.forEach(mesh => {
        this.setPointScale(mesh);
      });
    });
  }
}
